/**
 * Yeni Şablon — the reference's `data-screen="newTemplate"`.
 *
 * Ported against the rendered DOM of `Screens.newTemplate` (`m08d-wizard.js`): the static page
 * head from `screens.html`, the seven-cell `.stepper`, and one card per step in `#wizardBody`.
 * All seven steps are here — Temel bilgiler, Belge türü, Alıcı ve izinler, Tasarım, Canvas ölçüsü,
 * Alanlar, Önizleme — because the stepper is a four-column grid and the reference's own layout
 * wraps them 4 + 3; a shorter wizard is a different screen.
 *
 * The wizard is one mutable draft with explicit re-render points, which is what `WIZ` plus
 * `Screens.newTemplate()` is upstream, and the shape is kept rather than translated: the draft
 * lives in a module-level store so leaving the screen and coming back keeps a half-finished
 * template, the inputs are uncontrolled and read on «Devam» exactly where the reference reads
 * them, and the step subtree is re-keyed on each re-render so it is rebuilt from the draft the
 * same way `innerHTML` rebuilds it there. The controls that upstream changes WITHOUT re-rendering
 * (the type radios, the policy radios, the permission and per-field checkboxes) change nothing
 * here either, so a keystroke or a selection is never thrown away by a re-render the reference
 * would not have done.
 *
 * The wizard does not publish (`m08d-wizard.js` header, PO v0.7 §13): its last button creates a
 * DRAFT template and opens the editor, and the editor's checklist is the only publish gate.
 */
import { type ReactElement, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useV07Commands } from '../data/commands-context';
import { useV07Account } from '../data/session';
import { useSettings } from '../data/store';
import { useTemplateEmail } from '../data/template-email';
import { useTemplateLinkedInCard } from '../data/template-linkedin-card';
import type { SubjectType } from '../data/types';
import { ASSET_LIMITS, ingestImageFile, seedAssets } from '../lib/asset-library';
import { rememberPendingEmail } from '../lib/pending-email';
import {
  dataTypeRec,
  FIELD_DATA_TYPES,
  fieldLabel,
  PUBLICATION_POLICIES,
  VISIBILITY_TR,
} from '../lib/reference';
import { buildTerms, esc, typeRec } from '../lib/render';
import { checkTemplateCode, TEMPLATE_CODE_MAX, templateCodeFromName } from '../lib/template-code';
import { CREDENTIAL_TYPES, SUBJECT_TYPES } from '../lib/template-list';
import {
  assetRefOf,
  buildWizardLayout,
  CANVAS_PRESETS,
  catalogField,
  createWizardTemplate,
  defaultWizardFields,
  FIELD_CATALOG,
  FIT_MODES,
  initialWizard,
  isGraphicDataType,
  isSystemPrivate,
  makeBackground,
  makeCanvas,
  makeField,
  slugify,
  VISIBILITY,
  type Visibility,
  WIZ_STEPS,
  type WizardState,
  wizardPreviewSVG,
  wizHolderKey,
  wizPreviewData,
} from '../lib/wizard';
import { useToast } from '../ui/toast';
import { primeEditorDraft } from './EditorScreen';
import { CertificateEmailPanel } from './editor/CertificateEmailPanel';

/** `WIZ` — module-level upstream, and module-level here for the same reason: it survives the route. */
const WIZ: { current: WizardState } = { current: initialWizard() };

/** The step-4 card head prints the limit the way the reference computes it. */
const MAX_MB = Math.round(ASSET_LIMITS.maxBytes / 1048576);

const EXT_OF: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/svg+xml': 'svg',
};

const orientationTr = (w: number, h: number): string => (w === h ? 'kare' : w > h ? 'yatay' : 'dikey');

interface StepProps {
  wiz: WizardState;
  rerender: () => void;
  goBack: () => void;
  goNext: (commit?: () => boolean) => void;
}

/** `wizNav` — the reference's shared card footer; step 7 writes its own, as upstream does. */
function WizNav({
  backLabel,
  nextLabel,
  goBack,
  onNext,
}: {
  backLabel?: string;
  nextLabel?: string;
  goBack: () => void;
  onNext: () => void;
}): ReactElement {
  return (
    <div className="card-foot" style={{ justifyContent: 'space-between' }}>
      <button className="btn btn-ghost" type="button" id="wizBack" onClick={goBack}>
        {backLabel ?? 'Geri'}
      </button>
      <button className="btn btn-primary" type="button" id="wizNext" onClick={onNext}>
        {nextLabel ?? 'Devam'}
      </button>
    </div>
  );
}

// ------------------------------------------------------------------------------ 1 · Temel bilgiler

function Step1({ wiz, goBack, goNext }: StepProps): ReactElement {
  const toast = useToast();
  const [noExpiry, setNoExpiry] = useState(wiz.validityMode === 'none');
  const name = useRef<HTMLInputElement>(null);
  const code = useRef<HTMLInputElement>(null);
  const title = useRef<HTMLInputElement>(null);
  const descTr = useRef<HTMLTextAreaElement>(null);
  const descEn = useRef<HTMLTextAreaElement>(null);
  const critTr = useRef<HTMLTextAreaElement>(null);
  const critEn = useRef<HTMLTextAreaElement>(null);
  const lang = useRef<HTMLSelectElement>(null);
  const validity = useRef<HTMLInputElement>(null);

  /*
   * The short code, checked against the rule the BACKEND will apply — here, on the step that owns
   * the field.
   *
   * `codeError` is seeded from a refusal the server already made (`wiz.fieldError`), so a create
   * that came back «code — biçimi geçersiz» re-opens this step with the reason beside the input
   * rather than a toast on step seven.
   */
  const [codeError, setCodeError] = useState<string | null>(
    wiz.fieldError?.field === 'code' ? wiz.fieldError.message : null,
  );
  useEffect(() => {
    if (wiz.fieldError?.field !== 'code') return;
    code.current?.focus();
    code.current?.select();
    /* Read once: the author is looking at it now, and their next «Devam» decides it afresh. */
    wiz.fieldError = null;
  }, [wiz]);

  /* The reference writes every value into WIZ before it validates, so a «Devam» with an empty name
     still leaves the derived short code behind — and the field shows it on the way back. */
  const commit = (): boolean => {
    wiz.name = (name.current?.value ?? '').trim();
    /*
     * Typed or derived, the value stored is one `template.create` accepts.
     *
     * It used to be `trUpper(...).slice(0, 10)`: the derived code was UPPER CASE, which the pattern
     * `^[a-z0-9][a-z0-9-]{1,63}$` refuses outright, and anything typed went through untouched and
     * silently cut to ten characters. `CXO100-SK` reached the server as `CXO100-SK` and was refused
     * after all seven steps. The normalised value is written back into the field, so what will be
     * sent is what the author can see — and a value nothing usable can be made of is refused here,
     * beside the input, rather than four steps later.
     */
    const typed = code.current?.value ?? '';
    const checked = checkTemplateCode(typed.trim() ? typed : templateCodeFromName(wiz.name));
    wiz.code = checked.value;
    if (code.current) code.current.value = checked.value;
    setCodeError(checked.message);
    wiz.title = (title.current?.value ?? '').trim() || wiz.name;
    wiz.descTr = (descTr.current?.value ?? '').trim();
    wiz.descEn = (descEn.current?.value ?? '').trim();
    wiz.critTr = (critTr.current?.value ?? '').trim();
    wiz.critEn = (critEn.current?.value ?? '').trim();
    wiz.lang = lang.current?.value === 'en' ? 'en' : 'tr';
    wiz.validityMonths =
      wiz.validityMode === 'none' ? null : Number.parseInt(validity.current?.value ?? '', 10) || 12;
    if (!wiz.name) {
      toast('Şablon adı gerekli');
      return false;
    }
    /* The reason is already beside the field; the step simply does not advance past it. */
    if (checked.message !== null) {
      code.current?.focus();
      return false;
    }
    return true;
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>1. Temel bilgiler</h2>
        <span className="small muted">Sistemde sınırsız şablon oluşturabilirsiniz</span>
      </div>
      <div className="card-pad stack" style={{ gap: '12px' }}>
        <div className="field-row">
          <div className="field">
            <label htmlFor="w_name">Şablon adı *</label>
            <input
              className="input"
              id="w_name"
              defaultValue={wiz.name}
              placeholder="ör. Liderlik Gelişim Diploması"
              ref={name}
            />
          </div>
          <div className="field">
            <label htmlFor="w_code">Kısa kod</label>
            <input
              className="input"
              id="w_code"
              defaultValue={wiz.code}
              placeholder="lider"
              maxLength={TEMPLATE_CODE_MAX}
              aria-invalid={codeError ? true : undefined}
              aria-describedby={codeError ? 'w_codeErr' : 'w_codeHint'}
              ref={code}
            />
            {codeError ? (
              <div className="help" id="w_codeErr" role="alert" data-hpw-field-error="code">
                {codeError}
              </div>
            ) : (
              <div className="help" id="w_codeHint">
                Küçük harf, rakam ve tire; boş bırakırsanız şablon adından türetilir.
              </div>
            )}
          </div>
        </div>
        <div className="field">
          <label htmlFor="w_title">Credential başlığı (LinkedIn ve public sayfada görünür)</label>
          <input
            className="input"
            id="w_title"
            defaultValue={wiz.title}
            placeholder="ör. Leadership Development Diploma 2026"
            ref={title}
          />
        </div>
        <div className="field">
          <label htmlFor="w_descTr">Açıklama (TR)</label>
          <textarea
            className="textarea"
            id="w_descTr"
            style={{ minHeight: '70px' }}
            defaultValue={wiz.descTr}
            ref={descTr}
          />
        </div>
        <div className="field">
          <label htmlFor="w_descEn">Açıklama (EN)</label>
          <textarea
            className="textarea"
            id="w_descEn"
            style={{ minHeight: '60px' }}
            defaultValue={wiz.descEn}
            ref={descEn}
          />
        </div>
        <div className="field">
          <label htmlFor="w_critTr">Verilme kriteri (TR)</label>
          <textarea
            className="textarea"
            id="w_critTr"
            style={{ minHeight: '60px' }}
            defaultValue={wiz.critTr}
            ref={critTr}
          />
        </div>
        <div className="field">
          <label htmlFor="w_critEn">Verilme kriteri (EN)</label>
          <textarea
            className="textarea"
            id="w_critEn"
            style={{ minHeight: '50px' }}
            defaultValue={wiz.critEn}
            ref={critEn}
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="w_lang">Varsayılan dil</label>
            <select className="select" id="w_lang" defaultValue={wiz.lang} ref={lang}>
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="w_validity">Geçerlilik süresi (ay)</label>
            <input
              className="input"
              id="w_validity"
              type="number"
              min="1"
              max="600"
              defaultValue={wiz.validityMonths ?? ''}
              disabled={noExpiry}
              ref={validity}
            />
          </div>
        </div>
        <label className="check">
          <input
            type="checkbox"
            id="w_noexp"
            defaultChecked={wiz.validityMode === 'none'}
            onChange={(e) => {
              wiz.validityMode = e.target.checked ? 'none' : 'months';
              setNoExpiry(e.target.checked);
            }}
          />
          {' Süresiz (geçerlilik tarihi yok)'}
        </label>
      </div>
      <WizNav backLabel="İptal" nextLabel="Devam" goBack={goBack} onNext={() => goNext(commit)} />
    </div>
  );
}

// --------------------------------------------------------------------------------- 2 · Belge türü

function Step2({ wiz, goBack, goNext }: StepProps): ReactElement {
  const toast = useToast();
  const [custom, setCustom] = useState(wiz.credentialType === 'custom');
  const label = useRef<HTMLInputElement>(null);

  const commit = (): boolean => {
    if (label.current) wiz.customTypeLabel = label.current.value.trim();
    if (wiz.credentialType === 'custom' && !wiz.customTypeLabel) {
      toast('Özel tür için görünen ad girin');
      return false;
    }
    return true;
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>2. Belge türü</h2>
        <span className="small muted">Tür, canvas ölçüsünü zorunlu kılmaz</span>
      </div>
      <div className="card-pad">
        <div className="type-grid">
          {CREDENTIAL_TYPES.map((x) => (
            <label className="radio" key={x.key}>
              <input
                type="radio"
                name="wtype"
                value={x.key}
                defaultChecked={wiz.credentialType === x.key}
                onChange={() => {
                  wiz.credentialType = x.key;
                  setCustom(x.key === 'custom');
                }}
              />
              <span>
                <b>{x.tr}</b>
                <span>{x.en}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="field" id="w_customWrap" style={{ marginTop: '12px' }} hidden={!custom}>
          <label htmlFor="w_custom">Görünen tür adı</label>
          <input
            className="input"
            id="w_custom"
            defaultValue={wiz.customTypeLabel}
            placeholder="ör. onur beratı"
            ref={label}
          />
          <div className="help">
            {'Public sayfada «Bu '}
            <b>onur beratı</b>
            {' nedir?» gibi metinlerde bu ad kullanılır.'}
          </div>
        </div>
        <div className="info-box" style={{ marginTop: '12px' }}>
          Dijital rozet kare olmak zorunda değildir, sertifika yatay olmak zorunda değildir, diploma A4 olmak
          zorunda değildir. Ölçüyü 5. adımda serbestçe seçersiniz.
        </div>
      </div>
      <WizNav goBack={goBack} onNext={() => goNext(commit)} />
    </div>
  );
}

// ------------------------------------------------------------------------- 3 · Alıcı ve izinler

function Step3({ wiz, rerender, goBack, goNext }: StepProps): ReactElement {
  const orgLock = wiz.subjectType === 'organization';
  /* Domain invariant (rule 13): a kuruluş credential never offers the personal profile-add link,
     so the permission is forced off before the card is drawn, not only when it is saved. */
  if (orgLock) wiz.perms.allowLinkedInProfile = false;

  return (
    <div className="card">
      <div className="card-head">
        <h2>3. Alıcı türü, yayın politikası ve izinler</h2>
      </div>
      <div className="card-pad stack" style={{ gap: '14px' }}>
        <div className="type-grid">
          {SUBJECT_TYPES.map((x) => (
            <label className="radio" key={x.key}>
              <input
                type="radio"
                name="wholder"
                value={x.key}
                checked={wiz.subjectType === x.key}
                onChange={() => {
                  wiz.subjectType = x.key as SubjectType;
                  const oldKey = wiz.subjectType === 'organization' ? 'recipient_name' : 'organization_name';
                  const newKey = wizHolderKey(wiz.subjectType);
                  wiz.fields = wiz.fields.filter((f) => f.key !== oldKey);
                  if (!wiz.fields.some((f) => f.key === newKey))
                    wiz.fields.unshift(makeField(newKey, { required: true, importable: true }));
                  rerender();
                }}
              />
              <span>
                <b>{x.tr}</b>
                <span>
                  {x.key === 'person' ? 'Sahip alanı: Alıcı Adı' : 'Sahip alanı: Şirket / Kurum Adı'}
                </span>
              </span>
            </label>
          ))}
        </div>
        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="label">Yayın politikası</legend>
          <div className="stack" style={{ gap: '6px' }}>
            {PUBLICATION_POLICIES.map((p) => (
              <label className="radio" key={p.key}>
                <input
                  type="radio"
                  name="wpol"
                  value={p.key}
                  defaultChecked={wiz.publicationPolicy === p.key}
                  onChange={() => {
                    wiz.publicationPolicy = p.key;
                  }}
                />
                <span>
                  <b>{p.tr}</b>
                  <span>{p.help}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="stack" style={{ gap: '6px' }}>
          <label className="check">
            <input
              type="checkbox"
              data-perm="allowLinkedInProfile"
              defaultChecked={wiz.perms.allowLinkedInProfile}
              disabled={orgLock}
              onChange={(e) => {
                wiz.perms.allowLinkedInProfile = e.target.checked;
              }}
            />
            {orgLock
              ? ' LinkedIn profiline ekleme bağlantısı göster '
              : ' LinkedIn profiline ekleme bağlantısı göster'}
            {orgLock ? <span className="small muted">— kuruluş credential'ında kapalıdır</span> : null}
          </label>
          <label className="check">
            <input
              type="checkbox"
              data-perm="allowLinkedInShare"
              defaultChecked={wiz.perms.allowLinkedInShare}
              onChange={(e) => {
                wiz.perms.allowLinkedInShare = e.target.checked;
              }}
            />
            {" LinkedIn'de gönderi olarak paylaşma"}
          </label>
          <label className="check">
            <input
              type="checkbox"
              data-perm="allowDownload"
              defaultChecked={wiz.perms.allowDownload}
              onChange={(e) => {
                wiz.perms.allowDownload = e.target.checked;
              }}
            />
            {' PNG indirme ve yazdırma'}
          </label>
        </div>
        <div className="help">
          LinkedIn'in Lisanslar ve Sertifikalar bölümü kişisel profil kaydıdır; bir kuruluş credential'ı oraya
          eklenemez. Bu nedenle alıcı türü «şirket / kurum» iken profil ekleme izni domain düzeyinde kapalıdır
          ve yayın doğrulaması true değerini reddeder. Bağlantı her durumda LinkedIn'in resmî statik formudur;
          alanlar otomatik doldurulmaz.
        </div>
      </div>
      <WizNav goBack={goBack} onNext={() => goNext()} />
    </div>
  );
}

// ----------------------------------------------------------------------------------- 4 · Tasarım

function Step4({ wiz, rerender, goBack, goNext }: StepProps): ReactElement {
  const [over, setOver] = useState(false);
  const a = wiz.asset;

  /* API GAP: a real backend ingests the file, scans it and returns a stored asset id. Here the
     bytes never leave the tab — the digest, the dimensions and the alpha channel are measured in
     the browser by the same pipeline the Görsel Kütüphanesi screen uses. */
  const handle = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    wiz.assetError = null;
    try {
      const r = await ingestImageFile(file, seedAssets());
      wiz.asset = r.asset;
      wiz.canvasW = r.asset.width;
      wiz.canvasH = r.asset.height;
      wiz.canvasPreset = 'native';
      wiz.fitMode = 'native';
    } catch (e) {
      wiz.assetError = e instanceof Error ? e.message : String(e);
    }
    rerender();
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>4. Tasarım dosyası</h2>
        <span className="small muted">{`PNG · JPG · SVG · en çok ${MAX_MB} MB`}</span>
      </div>
      <div className="card-pad stack" style={{ gap: '12px' }}>
        <label
          className={`dropzone${over ? ' is-over' : ''}`}
          id="wzDz"
          htmlFor="w_file"
          onDragEnter={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void handle(e.dataTransfer.files[0]);
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <path d="M12 16V5" />
            <path d="M7 10l5-5 5 5" />
            <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
          </svg>
          <b>Tasarımı buraya bırakın ya da seçin</b>
          <span className="small">
            Gerçek genişlik, yükseklik, dosya türü, boyut, SHA-256, şeffaflık ve en-boy oranı otomatik
            algılanır
          </span>
        </label>
        <input
          type="file"
          id="w_file"
          accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
          hidden
          onChange={(e) => void handle(e.target.files?.[0])}
        />
        {wiz.assetError ? <div className="warn-box">{wiz.assetError}</div> : null}
        {a ? (
          <div className="split">
            <div className="card-pad" style={{ background: 'var(--surface-3)', borderRadius: '6px' }}>
              <img
                src={a.dataUrlForPrototype}
                alt="Yüklenen tasarım"
                style={{ maxWidth: '100%', display: 'block', margin: 'auto' }}
              />
            </div>
            {/* The reference's dl reads `fileName` and `extension`, which its own ingestion never
                writes; the two fields it does write are shown instead, in the same rows. */}
            <dl className="dl">
              <dt>Dosya</dt>
              <dd>{a.originalFileName}</dd>
              <dt>Tür</dt>
              <dd>{`${a.mimeType} (.${EXT_OF[a.mimeType] ?? ''})`}</dd>
              <dt>Boyut</dt>
              <dd className="tnum">{`${a.width} × ${a.height} px`}</dd>
              <dt>En-boy oranı</dt>
              <dd className="tnum">{a.aspectRatio}</dd>
              <dt>Dosya büyüklüğü</dt>
              <dd className="tnum">{`${(a.byteSize / 1024).toFixed(1)} KB`}</dd>
              <dt>Şeffaflık</dt>
              <dd>{a.hasAlpha ? 'var (alfa kanalı)' : 'yok'}</dd>
              <dt>SHA-256</dt>
              <dd className="small mono" style={{ wordBreak: 'break-all' }}>
                {a.sha256}
              </dd>
            </dl>
          </div>
        ) : null}
        <div className="help">
          Yüklenen dosyanın önceden tanımlı bir hash listesinde olması gerekmez. Kabul için yalnız şunlar
          denetlenir: geçerli görsel imzası, uzantı–MIME uyumu, boyut sınırı ve SVG içeriğinin zararlı
          öğelerden arındırılması. Hash yükleme anında hesaplanır ve bütünlük kontrolü için saklanır.
        </div>
        <div className="help">
          Tasarım dosyası olmadan da devam edebilirsiniz; arka planı sonradan editörden ekleyebilirsiniz.
        </div>
      </div>
      <WizNav goBack={goBack} onNext={() => goNext()} />
    </div>
  );
}

// ------------------------------------------------------------------------------ 5 · Canvas ölçüsü

function Step5({ wiz, rerender, goBack, goNext }: StepProps): ReactElement {
  const a = wiz.asset;
  const cw = useRef<HTMLInputElement>(null);
  const ch = useRef<HTMLInputElement>(null);
  const preview = useRef<HTMLDivElement>(null);

  /* `sync()` upstream writes the two number inputs and redraws the preview WITHOUT rebuilding the
     card, which is why the «Yön / oran» readout beside them only catches up on the next full
     re-render. Kept, because a rebuild on every committed digit would take the focus with it. */
  const sync = useCallback(() => {
    if (cw.current) cw.current.value = String(wiz.canvasW);
    if (ch.current) ch.current.value = String(wiz.canvasH);
    if (preview.current && a) {
      preview.current.innerHTML = wizardPreviewSVG(
        {
          canvas: makeCanvas({ width: wiz.canvasW, height: wiz.canvasH }),
          background: makeBackground({ assetRef: assetRefOf(a), fitMode: wiz.fitMode, hasAlpha: a.hasAlpha }),
          fieldDefinitions: [],
          layers: [],
          safeAreas: [],
        },
        {},
        a,
      );
    }
  }, [a, wiz]);

  useEffect(() => {
    sync();
  }, [sync]);

  const commit = (src: 'w' | 'h'): void => {
    let w = Number.parseInt(cw.current?.value ?? '', 10) || wiz.canvasW;
    let h = Number.parseInt(ch.current?.value ?? '', 10) || wiz.canvasH;
    if (wiz.aspectLock) {
      const ar = wiz.canvasW / wiz.canvasH;
      if (src === 'w') h = Math.round(w / ar);
      else w = Math.round(h * ar);
    }
    wiz.canvasW = Math.min(8000, Math.max(16, w));
    wiz.canvasH = Math.min(8000, Math.max(16, h));
    wiz.canvasPreset = 'custom';
    sync();
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>5. Canvas ölçüsü</h2>
        <span className="small muted">
          {a ? `Yüklenen görsel ${a.width}×${a.height}` : 'Görsel yüklenmedi'}
        </span>
      </div>
      <div className="card-pad stack" style={{ gap: '12px' }}>
        <div className="field">
          <label htmlFor="w_preset">Ölçü</label>
          <select
            className="select"
            id="w_preset"
            defaultValue={wiz.canvasPreset}
            onChange={(e) => {
              wiz.canvasPreset = e.target.value;
              const p = CANVAS_PRESETS.find((x) => x.key === e.target.value);
              if (p?.width && p.height) {
                wiz.canvasW = p.width;
                wiz.canvasH = p.height;
              } else if (e.target.value === 'native' && a) {
                wiz.canvasW = a.width;
                wiz.canvasH = a.height;
              }
              rerender();
            }}
          >
            {CANVAS_PRESETS.map((p) => (
              <option value={p.key} key={p.key}>
                {`${p.tr}${p.width ? ` (${p.width}×${p.height})` : ''}`}
              </option>
            ))}
          </select>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="w_cw">Genişlik (px)</label>
            <input
              className="input"
              id="w_cw"
              type="number"
              min="16"
              max="8000"
              defaultValue={wiz.canvasW}
              ref={cw}
              onChange={() => commit('w')}
            />
          </div>
          <div className="field">
            <label htmlFor="w_chh">Yükseklik (px)</label>
            <input
              className="input"
              id="w_chh"
              type="number"
              min="16"
              max="8000"
              defaultValue={wiz.canvasH}
              ref={ch}
              onChange={() => commit('h')}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
          <label className="check">
            <input
              type="checkbox"
              id="w_lock"
              defaultChecked={wiz.aspectLock}
              onChange={(e) => {
                wiz.aspectLock = e.target.checked;
              }}
            />
            {' En-boy oranını kilitle'}
          </label>
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            id="w_swap"
            onClick={() => {
              const t = wiz.canvasW;
              wiz.canvasW = wiz.canvasH;
              wiz.canvasH = t;
              rerender();
            }}
          >
            Yönü çevir
          </button>
          <span className="small muted">
            {'Yön: '}
            <b>{orientationTr(wiz.canvasW, wiz.canvasH)}</b>
            {` · oran ${(wiz.canvasW / wiz.canvasH).toFixed(3)}`}
          </span>
        </div>
        <div className="field">
          <label htmlFor="w_fit">Arka plan yerleşimi</label>
          <select
            className="select"
            id="w_fit"
            defaultValue={wiz.fitMode}
            onChange={(e) => {
              wiz.fitMode = e.target.value;
              sync();
            }}
          >
            {FIT_MODES.map((f) => (
              <option value={f.key} key={f.key}>
                {f.tr}
              </option>
            ))}
          </select>
          <div className="help">
            «Esnet» varsayılan değildir; görselin oranını bozar ve yalnız açık tercihle seçilir.
          </div>
        </div>
        {a ? (
          <div className="card-pad" style={{ background: 'var(--surface-3)', borderRadius: '6px' }}>
            <div id="w_canvasPrev" ref={preview} />
          </div>
        ) : null}
      </div>
      <WizNav goBack={goBack} onNext={() => goNext()} />
    </div>
  );
}

// ------------------------------------------------------------------------------------ 6 · Alanlar

function Step6({ wiz, rerender, goBack, goNext }: StepProps): ReactElement {
  const toast = useToast();
  const hk = wizHolderKey(wiz.subjectType);
  const used = new Set(wiz.fields.map((f) => f.key));
  const addable = FIELD_CATALOG.filter((f) => !used.has(f.key));
  const catalogPick = useRef<HTMLSelectElement>(null);
  const nfLabel = useRef<HTMLInputElement>(null);
  const nfKey = useRef<HTMLInputElement>(null);
  const nfType = useRef<HTMLSelectElement>(null);

  const commit = (): boolean => {
    if (!wiz.fields.some((f) => f.key === hk)) {
      toast('Sahip alanı gerekli');
      return false;
    }
    wiz.built = buildWizardLayout(wiz);
    return true;
  };

  const addCustom = (): void => {
    const label = nfLabel.current?.value.trim() ?? '';
    const key = (nfKey.current?.value.trim() || slugify(label).replace(/\./g, '_'))
      .replace(/[^a-z0-9_]/gi, '_')
      .toLowerCase();
    const type = nfType.current?.value ?? 'text';
    if (!label || !key) {
      toast('Ad ve anahtar gerekli');
      return;
    }
    if (wiz.fields.some((f) => f.key === key)) {
      toast('Bu anahtar zaten var');
      return;
    }
    wiz.fields.push(
      makeField(
        { key, tr: label, en: label, dataType: type, sample: label },
        {
          importable: !isGraphicDataType(type),
          visibility: type === 'email' ? 'private' : 'public_metadata_and_render',
          custom: true,
        },
      ),
    );
    wiz.customDraft = { label: '', key: '', type };
    toast(`«${label}» alanı eklendi`);
    rerender();
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>6. Dinamik alanlar</h2>
        <span className="small muted">İhtiyacınız kadar alan ekleyin — kod değişikliği gerekmez</span>
      </div>
      <div className="card-pad stack" style={{ gap: '12px' }}>
        <div className="stack" style={{ gap: '6px' }}>
          {wiz.fields.map((f) => {
            const sysPriv = isSystemPrivate(f.key);
            return (
              <div className="fld-item" key={f.id}>
                <div className="row">
                  <b>{fieldLabel(f, 'tr')}</b>
                  <span className="small muted mono">{f.key}</span>
                  <span className="pill pill-neutral none">{dataTypeRec(f.dataType).tr}</span>
                  {f.auto ? <span className="pill pill-info none">otomatik</span> : null}
                  {f.key === hk ? <span className="pill pill-active none">sahip alanı</span> : null}
                  {sysPriv ? <span className="pill pill-neutral none">sistem · daima gizli</span> : null}
                </div>
                <div className="row">
                  <label className="check">
                    <input
                      type="checkbox"
                      data-wf="required"
                      data-k={f.key}
                      defaultChecked={f.required}
                      disabled={f.key === hk}
                      onChange={(e) => {
                        f.required = e.target.checked;
                      }}
                    />
                    {' Zorunlu'}
                  </label>
                  <label className="check">
                    <input
                      type="checkbox"
                      data-wf="importable"
                      data-k={f.key}
                      defaultChecked={f.importable}
                      disabled={f.auto && f.key !== 'certification_period'}
                      onChange={(e) => {
                        f.importable = e.target.checked;
                      }}
                    />
                    {" Excel'den gelir"}
                  </label>
                  <label className="field inline" style={{ margin: 0 }}>
                    <span className="small muted">Görünürlük</span>
                    <select
                      className="select sm"
                      data-wvis={f.key}
                      disabled={sysPriv}
                      aria-label={`${fieldLabel(f, 'tr')} görünürlüğü`}
                      defaultValue={f.visibility}
                      onChange={(e) => {
                        f.visibility = e.target.value as Visibility;
                        wiz.built = null;
                      }}
                    >
                      {VISIBILITY.map((v) => (
                        <option value={v} key={v}>
                          {VISIBILITY_TR[v]}
                        </option>
                      ))}
                    </select>
                  </label>
                  {f.key === hk ? null : (
                    <button
                      className="icon-btn xs"
                      type="button"
                      data-wdel={f.key}
                      title="Kaldır"
                      onClick={() => {
                        wiz.fields = wiz.fields.filter((x) => x.key !== f.key);
                        rerender();
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="divider" />
        {addable.length ? (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="w_addf">Katalogdan alan ekle</label>
              <select className="select" id="w_addf" ref={catalogPick}>
                {addable.map((f) => (
                  <option value={f.key} key={f.key}>
                    {`${f.tr} · ${dataTypeRec(f.dataType).tr}`}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-secondary"
              type="button"
              id="w_addfBtn"
              onClick={() => {
                const k = catalogPick.current?.value ?? '';
                const cf = catalogField(k);
                if (!cf) return;
                wiz.fields.push(makeField(k, { importable: !cf.auto }));
                rerender();
              }}
            >
              Ekle
            </button>
          </div>
        ) : null}
        <div className="field-row">
          <div className="field">
            <label htmlFor="w_nfLabel">Özel alan adı</label>
            <input
              className="input"
              id="w_nfLabel"
              defaultValue={wiz.customDraft.label}
              placeholder="ör. Şube Kodu"
              ref={nfLabel}
            />
          </div>
          <div className="field">
            <label htmlFor="w_nfKey">Anahtar</label>
            <input
              className="input"
              id="w_nfKey"
              defaultValue={wiz.customDraft.key}
              placeholder="sube_kodu"
              ref={nfKey}
            />
          </div>
          <div className="field">
            <label htmlFor="w_nfType">Tür</label>
            <select className="select" id="w_nfType" defaultValue={wiz.customDraft.type} ref={nfType}>
              {FIELD_DATA_TYPES.filter(
                (x) => x.key !== 'credential_id' && x.key !== 'credential_url' && x.key !== 'qr',
              ).map((x) => (
                <option value={x.key} key={x.key}>
                  {x.tr}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          type="button"
          id="w_addCustom"
          style={{ justifySelf: 'start' }}
          onClick={addCustom}
        >
          Özel alan oluştur
        </button>
        <div className="help">
          Seçtiğiniz alanlar için başlangıç katmanları otomatik yerleştirilir; sonraki adımda ve editörde
          serbestçe taşıyabilirsiniz.
        </div>
      </div>
      <WizNav goBack={goBack} onNext={() => goNext(commit)} />
    </div>
  );
}

// ------------------------------------------------------------------------------------ 7 · E-posta

/**
 * «Bu sertifikayla gönderilecek e-posta», one step before the certificate is created.
 *
 * It renders the SAME panel the editor's «E-posta» button opens, over the same content — there is
 * one editing component in this product, not two. What differs is only that the certificate does
 * not exist yet, so the panel has no id to read the standard message against and says so; what the
 * author writes is carried with the rest of the wizard's work and saved against the new certificate
 * the moment it is created.
 */
function Step7({ wiz, goBack, goNext }: StepProps): ReactElement {
  const port = useTemplateEmail();
  const settings = useSettings();
  /*
   * What the certificate is, as far as the first six steps have decided.
   *
   * The server composes the standard message from this — the title it will carry, whether it is a
   * person's or an organization's, and the act its publication policy really performs — together
   * with the issuer profile, which is read there rather than sent from here. Memoised on the values
   * themselves so going back a step and changing the title re-composes the message, while simply
   * re-rendering does not.
   */
  const context = useMemo(
    () => ({
      title: wiz.title || wiz.name,
      credentialType: wiz.subjectType,
      publicationPolicy: wiz.publicationPolicy,
      issuerProfileId: settings.issuerProfileId ?? null,
    }),
    [wiz.title, wiz.name, wiz.subjectType, wiz.publicationPolicy, settings.issuerProfileId],
  );
  return (
    <div className="card">
      <div className="card-head">
        <h2>7. E-posta</h2>
        <span className="small muted">Bir kez hazırlayın, herkese kendi bilgileriyle gitsin</span>
      </div>
      <div className="card-pad">
        <CertificateEmailPanel
          port={port}
          templateId={null}
          context={context}
          draft={wiz.emailDraft}
          /*
           * The draft is REMEMBERED here, not re-rendered from here.
           *
           * `rerender()` re-keys the whole step (`<Step key={step:tick}>`), which rebuilds this
           * card from scratch — and rebuilding the panel on every keystroke threw away everything
           * the panel holds but the wizard does not: the open tab (typing in «HTML» flipped back
           * to «METİN» on the first character), the focus and caret, the cleaned source the
           * preview is drawn from, and the message under the buttons. That is the re-render the
           * reference does not do, and the rule this file states in its own header: a control
           * upstream changes WITHOUT re-rendering changes nothing here either. So the draft is
           * written to the wizard's mutable store and the panel is left alone; the next real
           * re-render — «Devam», «Geri», «Taslak kaydet» — reads it from there.
           */
          onDraftChange={(d) => {
            wiz.emailDraft = d;
          }}
          heading="Bu sertifikayla gönderilecek e-posta"
        />
      </div>
      <WizNav goBack={goBack} onNext={() => goNext()} />
    </div>
  );
}

// ---------------------------------------------------------------------------------- 8 · Önizleme

/**
 * Which step owns which of the fields `template.create` can refuse.
 *
 * Only the ones this wizard actually collects: a refusal naming anything else stays a message,
 * because sending the author to a step that does not hold the field would be worse than saying so.
 */
const STEP_OF_FIELD: Record<string, number> = {
  code: 1,
  name: 1,
  canonicalTitle: 1,
  credentialType: 2,
  publicationPolicy: 3,
};

/** The server's refusal, in this product's words, for the field it named. */
function fieldRefusal(detail: { key: string; code: string; message: string }): string {
  if (detail.key === 'code')
    return 'Sunucu bu kısa kodu kabul etmedi: yalnız küçük harf, rakam ve tire kullanılabilir, harf veya rakamla başlamalı ve 2–64 karakter olmalı.';
  if (detail.code === 'unique_violation' || detail.code === 'conflict')
    return 'Bu değer başka bir şablonda kullanılıyor.';
  return `Sunucu bu alanı kabul etmedi: ${detail.message}`;
}

function Step8({ wiz, rerender, goBack }: StepProps): ReactElement {
  const settings = useSettings();
  const commands = useV07Commands();
  const navigate = useNavigate();
  const toast = useToast();
  const linkedInCard = useTemplateLinkedInCard();
  /* Who a half-saved design is kept for, so a reload can give it back to them and to nobody else. */
  const ownerKey = useV07Account()?.ownerKey ?? null;

  /* «Taslak olarak oluştur» in flight: a second click is the same intent, not a second template. */
  const creating = useRef(false);
  const templateEmail = useTemplateEmail();

  /**
   * The e-mail step's work, saved against the certificate that has just been created.
   *
   * It runs AFTER the template exists, because an e-mail belongs to a template and there was
   * nothing to attach it to until now. A refusal here does not undo the certificate — it is
   * reported, and the editor's «E-posta» button is where it is fixed; claiming the certificate
   * failed would be false, and silently dropping the message would be worse.
   */
  const saveCarriedEmail = async (templateId: string): Promise<string | null> => {
    if (!templateEmail || !wiz.emailDraft) return null;
    try {
      await templateEmail.save(templateId, wiz.emailDraft);
      return null;
    } catch (e) {
      /*
       * The message is NOT lost with the wizard.
       *
       * The wizard resets itself after creating the certificate, and it used to do that even when
       * this save had failed — so the certificate existed, the e-mail did not, and there was
       * nothing left to retry from. It is kept for this tab under the id the server gave; the
       * editor's «E-posta» panel restores it as an unsaved change on that SAME certificate, so
       * «Kaydet» finishes the job rather than the author writing it again.
       */
      rememberPendingEmail(templateId, wiz.emailDraft);
      return `Sertifika oluşturuldu; e-posta kaydedilemedi: ${(e as Error).message}. Yazdığınız içerik korundu — editördeki «E-posta» bölümünden kaydedebilirsiniz.`;
    }
  };

  /**
   * A carried LinkedIn arrangement, saved against the certificate that has just been created.
   *
   * THE WIZARD NO LONGER HAS A LINKEDIN STEP. It was added and then taken back out, because a
   * ninth chip in the stepper moved the preparation screen's own layout — the share image is
   * arranged from the editor's «LinkedIn görseli» button instead, after the certificate exists.
   *
   * This carry path stays anyway, and deliberately: `WIZ.current` outlives a single render and a
   * wizard that was open when the step disappeared can still be holding an arrangement somebody
   * typed. Dropping it silently is the one thing that must not happen, so if a draft is there it
   * is written to the new certificate exactly as before. With no step to produce one this is
   * normally a no-op, and its cost is a single null check.
   *
   * A refusal does not undo the certificate and does not undo the e-mail — it is reported, and
   * the editor's «LinkedIn görseli» button is where the image is arranged. Until then the record
   * simply keeps the automatic card, which is the behaviour it would have had anyway.
   */
  const saveCarriedLinkedInCard = async (templateId: string): Promise<string | null> => {
    if (!linkedInCard || !wiz.linkedInCardDraft) return null;
    try {
      await linkedInCard.save(templateId, wiz.linkedInCardDraft);
      return null;
    } catch (e) {
      return `Sertifika oluşturuldu; LinkedIn görseli kaydedilemedi: ${(e as Error).message}. Editördeki «LinkedIn görseli» bölümünden yeniden düzenleyebilirsiniz.`;
    }
  };

  const layout = wiz.built ?? buildWizardLayout(wiz);
  wiz.built = layout;
  const terms = buildTerms(wiz.credentialType, wiz.customTypeLabel, 'tr');
  const svg = wizardPreviewSVG(layout, wizPreviewData(wiz, wiz.preview, settings.publicBase), wiz.asset);
  const title = wiz.title || wiz.name;

  const finish = async (): Promise<void> => {
    /* One press, one template: a second click while the first is in flight is the same intent. */
    if (creating.current) return;
    creating.current = true;
    const t = createWizardTemplate(wiz, layout);
    const r = await commands.createTemplate({ template: t });
    creating.current = false;
    /*
     * A template that WAS created, whose design was not saved, is not «could not be created».
     *
     * Creating the template and storing the seven steps' work are two requests. When the second one
     * failed the wizard said the template could not be created and stayed where it was, while the
     * template sat in the register carrying the backend's default draft — so the person who had
     * just been told nothing happened had, in fact, made something they could not see. The message
     * now says what exists and what did not get saved, and the editor is opened on it, because that
     * is the one screen from which the design can be saved again.
     */
    if (r.partial === 'template_created_without_draft') {
      /*
       * The template exists; its design does not. Hand the editor the seven steps' work as an
       * UNSAVED copy under the id the server gave, and only then let go of the wizard's own — so
       * the single local copy is never dropped before the recovery is in place. «Taslak Kaydet»
       * then writes it to that same template rather than making a second one.
       */
      const templateId = r.templateId ?? t.id;
      primeEditorDraft(templateId, { ...t, id: templateId }, ownerKey);
      const emailProblem = await saveCarriedEmail(templateId);
      const cardProblem = await saveCarriedLinkedInCard(templateId);
      toast(emailProblem ?? cardProblem ?? r.message ?? 'Şablon oluşturuldu, tasarımı kaydedilemedi');
      WIZ.current = initialWizard();
      void navigate(`/admin/templates/${templateId}/editor`);
      return;
    }
    if (!r.ok) {
      /*
       * A field the server named goes back to the step that owns it, with everything else kept.
       *
       * The seven steps' work lives in `WIZ.current` and is only reset on success, so nothing is
       * lost here — what was missing was the way back. A refusal that names `code` used to arrive
       * as a toast on this step, four steps away from the input it was about; now the wizard opens
       * that step, marks the field and says why, and the author fixes one value rather than
       * starting again.
       */
      const named = (r.details ?? []).find((d) => STEP_OF_FIELD[d.key] !== undefined);
      if (named) {
        wiz.fieldError = { field: named.key, message: fieldRefusal(named) };
        wiz.step = STEP_OF_FIELD[named.key] as number;
        rerender();
        return;
      }
      toast(r.message ?? 'Şablon oluşturulamadı');
      return;
    }
    const createdId = r.templateId ?? r.message ?? t.id;
    const emailProblem = await saveCarriedEmail(createdId);
    const cardProblem = await saveCarriedLinkedInCard(createdId);
    toast(
      emailProblem ??
        cardProblem ??
        `${t.name} taslak olarak oluşturuldu — yayın için editördeki kapıyı kullanın`,
    );
    /* `resetWizard()` — the next «+ Yeni Şablon» starts clean, as upstream. */
    WIZ.current = initialWizard();
    void navigate(`/admin/templates/${createdId}/editor`);
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>8. Önizleme ve taslak oluşturma</h2>
        <span className="small muted">{`${wiz.canvasW}×${wiz.canvasH} · ${terms.nounDef}`}</span>
      </div>
      <div className="card-pad stack" style={{ gap: '14px' }}>
        <div className="chips">
          {(['short', 'long', 'org'] as const).map((mode) => (
            <button
              className={`chip ${wiz.preview === mode ? 'is-on' : ''}`}
              type="button"
              data-wp={mode}
              key={mode}
              onClick={() => {
                wiz.preview = mode;
                rerender();
              }}
            >
              {mode === 'short' ? 'Kısa ad' : mode === 'long' ? 'Çok uzun ad' : 'Şirket adı'}
            </button>
          ))}
        </div>
        <div className="split">
          <div
            className="card-pad"
            style={{ background: 'var(--surface-3)', borderRadius: '6px' }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
          <div className="stack" style={{ gap: '10px' }}>
            <dl className="dl">
              <dt>Ad</dt>
              <dd>{wiz.name}</dd>
              <dt>Tür</dt>
              <dd>
                {wiz.credentialType === 'custom' ? wiz.customTypeLabel : typeRec(wiz.credentialType).tr}
              </dd>
              <dt>Alıcı</dt>
              <dd>{wiz.subjectType === 'organization' ? 'Şirket / kurum' : 'Kişi'}</dd>
              <dt>Canvas</dt>
              <dd className="tnum">
                {`${wiz.canvasW} × ${wiz.canvasH} (${orientationTr(wiz.canvasW, wiz.canvasH)})`}
              </dd>
              <dt>Arka plan</dt>
              <dd>
                {wiz.asset
                  ? `${wiz.asset.originalFileName} · ${wiz.asset.width}×${wiz.asset.height} · ${wiz.fitMode}`
                  : 'yok'}
              </dd>
              <dt>Alanlar</dt>
              <dd>{wiz.fields.map((f) => fieldLabel(f, 'tr')).join(', ')}</dd>
              <dt>Geçerlilik</dt>
              <dd>{wiz.validityMonths ? `${wiz.validityMonths} ay` : 'süresiz'}</dd>
            </dl>
            <div className="mini-previews">
              <div>
                <span className="eyebrow">Mobil public sayfa</span>
                {/* One innerHTML, because upstream concatenates the SVG string with the caption
                    inside the same element and a React child would need a wrapper it does not have. */}
                <div
                  className="mini-phone"
                  dangerouslySetInnerHTML={{
                    __html: `${svg}<div class="mini-meta"><b>${esc(title)}</b><span>${esc(terms.verified)}</span></div>`,
                  }}
                />
              </div>
              <div>
                <span className="eyebrow">E-posta</span>
                <div className="mini-mail">
                  <b>{terms.ready}</b>
                  <span className="small muted">{terms.claimCta}</span>
                </div>
              </div>
              <div>
                <span className="eyebrow">LinkedIn paylaşım görseli</span>
                {/* API GAP: the share card is a real PNG a backend renders (`og.png`); the raster
                    path is not part of the ported engine, so the same plan is shown as SVG. */}
                <div className="mini-og" id="wzOg" dangerouslySetInnerHTML={{ __html: svg }} />
              </div>
            </div>
          </div>
        </div>
        <div className="info-box">
          {'Sihirbaz yayın yapmaz. Şablon '}
          <b>taslak</b>
          {
            ' olarak oluşturulur ve editörde açılır; release yalnız editördeki yayın kapısından (kontrol listesi + açık yönetici onayı) çıkar. Yayınlanmamış şablonla toplu üretim yapılamaz.'
          }
        </div>
      </div>
      <div className="card-foot" style={{ justifyContent: 'space-between' }}>
        <button className="btn btn-ghost" type="button" id="wizBack" onClick={goBack}>
          Geri
        </button>
        <button className="btn btn-primary" type="button" id="wizSaveDraft" onClick={() => void finish()}>
          Taslak olarak oluştur ve editörü aç
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------------- the screen

const STEPS = [Step1, Step2, Step3, Step4, Step5, Step6, Step7, Step8];

/**
 * What the wizard shows instead of its first step when a template cannot be made yet.
 *
 * The reference's own page furniture — the back link, the head, the card — so this is the wizard
 * saying why, not a different screen.
 */
function WizardBlocked({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: { to: string; label: string };
}): ReactElement {
  return (
    <section data-screen="newTemplate">
      <div className="page-head">
        <div>
          <Link
            className="small"
            to="/admin/templates"
            style={{ color: 'var(--text-3)', textDecoration: 'none' }}
          >
            ← Şablonlar
          </Link>
          <h1>Yeni Şablon</h1>
        </div>
      </div>
      <div className="card">
        <div className="card-head">
          <h2>{title}</h2>
        </div>
        <div className="card-pad stack" style={{ gap: '12px' }}>
          <p className="lead">{body}</p>
          {action ? (
            <div>
              <Link className="btn btn-primary" to={action.to}>
                {action.label}
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export function NewTemplateScreen(): ReactElement {
  const navigate = useNavigate();
  const [tick, rerender] = useReducer((n: number) => n + 1, 0);
  const settings = useSettings();
  const wiz = WIZ.current;
  if (!wiz.fields.length) wiz.fields = defaultWizardFields(wiz.subjectType);

  /*
   * A template belongs to an issuer, so without an issuer profile there is nothing to create one
   * against — and the wizard used to let all seven steps be filled in before saying so, losing the
   * work at the last button. The refusal belongs where the journey starts (PO 7.11 §5).
   *
   * «Not readable» and «not there» are answered differently: one of them has a screen to go to.
   */
  if (settings.issuerProfileAccess === 'forbidden')
    return (
      <WizardBlocked
        title="Kurum profili görüntülenemiyor"
        body="Şablon oluşturmak için kurum profili gerekir, ancak bu hesabın kurum profilini görme yetkisi yok. Sahip hesabıyla giriş yapılması gerekir."
      />
    );
  if (!settings.issuerProfileId)
    return (
      <WizardBlocked
        title="Önce kurum profili oluşturun"
        body="Her şablon bir veren kuruma bağlıdır ve bu kurulumda henüz kurum profili yok. Kurum kimliğini bir kez tanımladığınızda şablon oluşturabilirsiniz."
        action={{ to: '/admin/settings/verification', label: 'Kurum ayarlarına git' }}
      />
    );

  const goBack = (): void => {
    if (wiz.step === 1) {
      void navigate('/admin/templates');
      return;
    }
    wiz.step -= 1;
    rerender();
  };

  const goNext = (commit?: () => boolean): void => {
    if (commit && commit() === false) return;
    wiz.step += 1;
    rerender();
  };

  const Step = STEPS[wiz.step - 1] ?? Step1;

  return (
    <section data-screen="newTemplate">
      <div className="page-head">
        <div>
          <Link
            className="small"
            to="/admin/templates"
            style={{ color: 'var(--text-3)', textDecoration: 'none' }}
          >
            ← Şablonlar
          </Link>
          <h1>Yeni Şablon</h1>
          <p className="lead">
            Rozet, sertifika, diploma, katılım belgesi, şirket sertifikası ya da tanımlayacağınız özel tür —
            her ölçüde, sınırsız sayıda.
          </p>
        </div>
      </div>
      <div className="stepper" id="wizardStepper">
        {WIZ_STEPS.map((s, i) => (
          <div
            className={`step ${wiz.step === i + 1 ? 'is-current' : wiz.step > i + 1 ? 'is-done' : ''}`}
            key={s}
          >
            <span className="n">{wiz.step > i + 1 ? '✓' : String(i + 1)}</span>
            <span>{s}</span>
          </div>
        ))}
      </div>
      {/* Re-keyed on every re-render, so the card is rebuilt from the draft exactly where upstream
          rewrites `#wizardBody`, and left alone everywhere upstream leaves the DOM alone. */}
      <div id="wizardBody">
        <Step key={`${wiz.step}:${tick}`} wiz={wiz} rerender={rerender} goBack={goBack} goNext={goNext} />
      </div>
    </section>
  );
}
